import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  IdCard, BadgeCheck, FileText, Umbrella, Camera,
  CheckCircle, XCircle, AlertTriangle, Eye, ChevronDown, ChevronRight,
  ExternalLink, Download, History, Trash2,
} from 'lucide-react';
import { UserAvatar } from '@/components/UserAvatar';
import { ContactRow } from '@/components/ContactRow';
import { formatDominicaPhone } from '@/lib/formatPhone';
import { formatDistanceToNow } from 'date-fns';
import CharterEligibilityToggle from '@/components/admin/CharterEligibilityToggle';

// ─── Types ───────────────────────────────────────────────────────────────────

type DocType = 'drivers_license' | 'psv_permit' | 'vehicle_registration'
  | 'insurance_certificate' | 'selfie_with_license';

type DocStatus = 'pending_review' | 'manual_review' | 'auto_approved'
  | 'approved' | 'rejected';

interface DriverDocument {
  id: string;
  driver_id: string;
  driver_name: string | null;
  driver_phone: string | null;
  driver_picture_url: string | null;
  license_plate: string | null;
  doc_type: DocType;
  status: DocStatus;
  storage_path: string | null;
  content_type: string | null;
  expiration_date: string | null;
  issue_date: string | null;
  document_number: string | null;
  full_name_on_doc: string | null;
  ai_confidence: number | null;
  ai_warnings: string[];
  ai_extraction: object | null;
  ai_provider: string | null;
  ai_processed_at: string | null;
  ai_error: string | null;
  rejection_reason: string | null;
  uploaded_at: string;
  updated_at: string;
  driver_verification_status: string;
  driver_verification_expires_at: string | null;
  // Attestation + submission method.
  //   • 'photo_and_manual' — current default for non-selfie docs.
  //     Driver typed the fields AND uploaded a photo. Review both.
  //   • 'photo'             — selfie or legacy photo-only row.
  //   • 'manual_entry'      — legacy typed-only row (no photo).
  submission_method?: 'photo' | 'manual_entry' | 'photo_and_manual';
  attestation_signed_at?: string | null;
  attestation_typed_name?: string | null;
  attestation_device?: string | null;
  attestation_ip?: string | null;
  government_database_match?: Record<string, unknown> | null;
}

interface PendingDriver {
  id: string;
  full_name: string | null;
  phone_number: string | null;
  profile_picture_url: string | null;
  license_plate: string | null;
  verification_status: string;
  verification_grace_until: string | null;
  verification_expires_at: string | null;
  days_until_grace_ends: number;
  docs_uploaded_count: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DOC_TYPE_LABELS: Record<DocType, string> = {
  drivers_license: "Driver's license",
  psv_permit: 'PSV permit',
  vehicle_registration: 'Vehicle registration',
  insurance_certificate: 'Insurance certificate',
  selfie_with_license: 'Selfie with license',
};

const DOC_TYPE_ICONS: Record<DocType, React.ElementType> = {
  drivers_license: IdCard,
  psv_permit: BadgeCheck,
  vehicle_registration: FileText,
  insurance_certificate: Umbrella,
  selfie_with_license: Camera,
};

const AI_WARNING_LABELS: Record<string, string> = {
  blurry: 'Photo is blurry',
  glare_obscures_text: 'Glare hides part of the text',
  photo_of_a_screen: 'User photographed a screen, not the original',
  wrong_country: 'Document is not from Dominica',
  wrong_document_type: "Document doesn't match the slot",
  no_expiration_visible: 'Expiration date not visible',
  document_appears_expired: 'Document appears already expired',
  name_not_legible: 'Name is not legible',
  tampered_or_edited_appearance: 'Image looks edited / tampered with',
  selfie_no_face: 'Selfie has no visible face',
  selfie_no_visible_document: 'Selfie does not show a license',
  insurance_appears_personal_only: 'Insurance is personal only and needs commercial coverage',
};

const STATUS_COLORS: Record<string, string> = {
  pending_review: 'bg-gray-500/10 text-gray-400 border-gray-600',
  manual_review: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  auto_approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const VERIFICATION_STATUS_COLORS: Record<string, string> = {
  pending_documents: 'bg-gray-500/10 text-gray-400 border-gray-600',
  pending_verification: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  verified: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  expired_document: 'bg-red-500/10 text-red-400 border-red-500/20',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
};

type FilterStatus = 'manual_review' | 'approved' | 'rejected' | null;

// ─── Main Component ──────────────────────────────────────────────────────────

export default function DriverVerification() {
  const [subView, setSubView] = useState<'queue' | 'pending'>('queue');
  const [documents, setDocuments] = useState<DriverDocument[]>([]);
  const [pendingDrivers, setPendingDrivers] = useState<PendingDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('manual_review');
  const [selectedDoc, setSelectedDoc] = useState<DriverDocument | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Reject modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDocId, setRejectDocId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('list_driver_documents_admin', {
        p_status: filterStatus,
        p_limit: 100,
        p_offset: 0,
      });
      if (error) throw error;
      const docs = data?.documents ?? (Array.isArray(data) ? data : []);
      setDocuments(docs.map((d: any) => ({
        ...d,
        ai_warnings: d.ai_warnings || [],
      })));
    } catch (err: any) {
      toast.error(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const fetchPendingDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('list_pending_verification_drivers_admin');
      if (error) throw error;
      const drivers = data?.drivers ?? (Array.isArray(data) ? data : []);
      setPendingDrivers(drivers);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load pending drivers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (subView === 'queue') {
      fetchDocuments();
    } else {
      fetchPendingDrivers();
    }
  }, [subView, fetchDocuments, fetchPendingDrivers]);

  // ─── Realtime ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const channel = supabase
      .channel('admin-driver-docs')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'driver_documents' },
        () => {
          if (subView === 'queue') fetchDocuments();
          else fetchPendingDrivers();
        })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [subView, fetchDocuments, fetchPendingDrivers]);

  // ─── Actions ───────────────────────────────────────────────────────────────

  const handleApprove = async (
    docId: string,
    overrides: { expiration?: string; docNumber?: string; fullName?: string }
  ) => {
    setActionLoading(true);
    try {
      const { data, error } = await supabase.rpc('approve_driver_document', {
        p_document_id: docId,
        p_expiration: overrides.expiration || null,
        p_document_number: overrides.docNumber || null,
        p_full_name: overrides.fullName || null,
      });
      if (error) throw error;
      if (data && !data.ok) throw new Error(data.error || 'Approval failed');
      toast.success('Document approved');
      setDrawerOpen(false);
      setSelectedDoc(null);
      fetchDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDocId || rejectReason.length < 5) return;
    setActionLoading(true);
    try {
      const { data, error } = await supabase.rpc('reject_driver_document', {
        p_document_id: rejectDocId,
        p_reason: rejectReason,
      });
      if (error) throw error;
      if (data && !data.ok) throw new Error(data.error || 'Rejection failed');
      toast.success('Document rejected');
      setRejectModalOpen(false);
      setRejectReason('');
      setRejectDocId(null);
      setDrawerOpen(false);
      setSelectedDoc(null);
      fetchDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (docId: string) => {
    setRejectDocId(docId);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Driver Verification</h1>
          <p className="text-sm text-gray-400 mt-1">
            Review uploaded documents and manage driver verification status
          </p>
        </div>
      </div>

      {/* Sub-view toggle */}
      <div className="flex gap-1 bg-gray-900/50 border border-gray-800 rounded-lg p-1 w-fit">
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            subView === 'queue'
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
          onClick={() => setSubView('queue')}
        >
          Document Queue
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            subView === 'pending'
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
          onClick={() => setSubView('pending')}
        >
          Pending Drivers
        </button>
      </div>

      {/* Sub-views */}
      {subView === 'queue' ? (
        <DocumentQueue
          documents={documents}
          loading={loading}
          filterStatus={filterStatus}
          onFilterChange={setFilterStatus}
          onReviewDriver={(driverId) => {
            setSelectedDriverId(driverId);
            setSelectedDoc(null);
            setDrawerOpen(true);
          }}
        />
      ) : (
        <PendingDriversView
          drivers={pendingDrivers}
          loading={loading}
          onSelectDriver={(driverId) => {
            setSelectedDriverId(driverId);
            setSelectedDoc(null);
            setDrawerOpen(true);
          }}
        />
      )}

      {/* Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[720px] bg-[#111827] border-gray-800 overflow-y-auto"
        >
          {selectedDoc && (
            <DocumentDetailDrawer
              doc={selectedDoc}
              onApprove={handleApprove}
              onReject={openRejectModal}
              actionLoading={actionLoading}
            />
          )}
          {selectedDriverId && !selectedDoc && (
            <DriverDocsDrawer
              driverId={selectedDriverId}
              onReviewDoc={(doc) => {
                setSelectedDoc(doc);
                setSelectedDriverId(null);
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Reject Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent className="bg-[#111827] border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white">Reject Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-gray-300">Reason for rejection (min 5 characters)</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Document is expired, image is unreadable..."
              className="bg-gray-900 border-gray-700 text-white"
              rows={3}
            />
            {rejectReason.length > 0 && rejectReason.length < 5 && (
              <p className="text-xs text-red-400">Reason must be at least 5 characters</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRejectModalOpen(false)}
              className="text-gray-400"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectReason.length < 5 || actionLoading}
            >
              {actionLoading ? 'Rejecting...' : 'Reject Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Document Queue Sub-view ─────────────────────────────────────────────────
//
// Rows are grouped by driver, not by document. If Doe John has 5
// approved docs, that's ONE row with a summary ("5 docs · 5 approved")
// — admin clicks Review and the existing DriverDocsDrawer opens to
// show all 5. Massively reduces the visual noise when a single driver
// has uploaded their full set.

interface DriverGroup {
  driver_id: string;
  driver_name: string | null;
  driver_phone: string | null;
  driver_picture_url: string | null;
  docs: DriverDocument[];
  /** Latest uploaded_at across docs in the group. */
  latest_uploaded_at: string;
  counts: {
    awaiting: number;
    approved: number;
    rejected: number;
    other: number;
    total: number;
  };
  hasManualEntry: boolean;
}

function groupByDriver(docs: DriverDocument[]): DriverGroup[] {
  const map = new Map<string, DriverGroup>();
  for (const doc of docs) {
    let g = map.get(doc.driver_id);
    if (!g) {
      g = {
        driver_id: doc.driver_id,
        driver_name: doc.driver_name,
        driver_phone: doc.driver_phone,
        driver_picture_url: doc.driver_picture_url,
        docs: [],
        latest_uploaded_at: doc.uploaded_at,
        counts: { awaiting: 0, approved: 0, rejected: 0, other: 0, total: 0 },
        hasManualEntry: false,
      };
      map.set(doc.driver_id, g);
    }
    g.docs.push(doc);
    g.counts.total += 1;
    if (doc.status === 'manual_review' || doc.status === 'pending_review') {
      g.counts.awaiting += 1;
    } else if (doc.status === 'approved' || doc.status === 'auto_approved') {
      g.counts.approved += 1;
    } else if (doc.status === 'rejected') {
      g.counts.rejected += 1;
    } else {
      g.counts.other += 1;
    }
    if (doc.submission_method === 'manual_entry') g.hasManualEntry = true;
    if (new Date(doc.uploaded_at) > new Date(g.latest_uploaded_at)) {
      g.latest_uploaded_at = doc.uploaded_at;
    }
  }
  // Most recent activity first.
  return Array.from(map.values()).sort(
    (a, b) =>
      new Date(b.latest_uploaded_at).getTime() -
      new Date(a.latest_uploaded_at).getTime(),
  );
}

function DocumentQueue({
  documents,
  loading,
  filterStatus,
  onFilterChange,
  onReviewDriver,
}: {
  documents: DriverDocument[];
  loading: boolean;
  filterStatus: FilterStatus;
  onFilterChange: (s: FilterStatus) => void;
  /** Open the driver-level drawer that lists all the driver's docs. */
  onReviewDriver: (driverId: string) => void;
}) {
  const filters: { label: string; value: FilterStatus }[] = [
    { label: 'Awaiting review', value: 'manual_review' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'All', value: null },
  ];

  const groups = groupByDriver(documents);

  return (
    <div className="space-y-4">
      {/* Filter chips */}
      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.label}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filterStatus === f.value
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                : 'bg-gray-900/50 text-gray-400 border-gray-700 hover:text-white hover:border-gray-600'
            }`}
            onClick={() => onFilterChange(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Driver-grouped table */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-14 bg-gray-800 rounded-lg" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-white font-medium">All caught up. No documents match this filter.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border border-gray-800 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400">Driver</TableHead>
                <TableHead className="text-gray-400">Documents</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400">Last activity</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((g) => {
                const docTypesInGroup = Array.from(
                  new Set(g.docs.map((d) => d.doc_type)),
                );
                return (
                  <TableRow
                    key={g.driver_id}
                    className="border-gray-800 hover:bg-gray-800/30 cursor-pointer"
                    onClick={() => onReviewDriver(g.driver_id)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <UserAvatar size="sm" name={g.driver_name} src={g.driver_picture_url} />
                        <div className="flex flex-col">
                          <span className="text-white text-sm">{g.driver_name || 'Unknown'}</span>
                          {g.driver_phone && (
                            <span className="text-xs text-gray-500 font-mono">
                              {formatDominicaPhone(g.driver_phone)}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-gray-300">
                          {g.counts.total}
                        </span>
                        <span className="text-xs text-gray-500">
                          {g.counts.total === 1 ? 'doc' : 'docs'}
                        </span>
                        <div className="ml-2 flex items-center gap-1">
                          {docTypesInGroup.slice(0, 5).map((t) => {
                            const Icon = DOC_TYPE_ICONS[t] || FileText;
                            return (
                              <span
                                key={t}
                                title={DOC_TYPE_LABELS[t]}
                                className="inline-flex h-5 w-5 items-center justify-center rounded bg-gray-800/60 border border-gray-700/50"
                              >
                                <Icon className="w-3 h-3 text-gray-400" />
                              </span>
                            );
                          })}
                        </div>
                        {g.hasManualEntry ? (
                          <span className="ml-1 inline-flex items-center gap-0.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-medium text-indigo-300">
                            Manual
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {g.counts.awaiting > 0 && (
                          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                            {g.counts.awaiting} awaiting
                          </Badge>
                        )}
                        {g.counts.approved > 0 && (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                            {g.counts.approved} approved
                          </Badge>
                        )}
                        {g.counts.rejected > 0 && (
                          <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                            {g.counts.rejected} rejected
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-400 text-sm">
                        {formatDistanceToNow(new Date(g.latest_uploaded_at), { addSuffix: true })}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-gray-700 text-gray-300 hover:text-white hover:border-emerald-500/50"
                        onClick={(e) => { e.stopPropagation(); onReviewDriver(g.driver_id); }}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" />
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ─── Pending Drivers Sub-view ────────────────────────────────────────────────

function PendingDriversView({
  drivers,
  loading,
  onSelectDriver,
}: {
  drivers: PendingDriver[];
  loading: boolean;
  onSelectDriver: (id: string) => void;
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-14 bg-gray-800 rounded-lg" />
        ))}
      </div>
    );
  }

  if (drivers.length === 0) {
    return (
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="py-12 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-white font-medium">All drivers verified</p>
          <p className="text-sm text-gray-400 mt-1">No pending verifications.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="border border-gray-800 rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-gray-800 hover:bg-transparent">
            <TableHead className="text-gray-400">Driver</TableHead>
            <TableHead className="text-gray-400">Status</TableHead>
            <TableHead className="text-gray-400">Docs Uploaded</TableHead>
            <TableHead className="text-gray-400">Grace Deadline</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {drivers.map((driver) => {
            const graceColor = driver.days_until_grace_ends <= 3
              ? 'text-red-400'
              : driver.days_until_grace_ends <= 7
                ? 'text-amber-400'
                : 'text-gray-300';

            return (
              <TableRow
                key={driver.id}
                className="border-gray-800 hover:bg-gray-800/30 cursor-pointer"
                onClick={() => onSelectDriver(driver.id)}
              >
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <UserAvatar size="sm" name={driver.full_name} src={driver.profile_picture_url} />
                    <div className="flex flex-col">
                      <span className="text-white text-sm">{driver.full_name || 'Unknown'}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        {driver.phone_number && (
                          <span className="text-xs text-gray-500 font-mono">
                            {formatDominicaPhone(driver.phone_number)}
                          </span>
                        )}
                        {driver.license_plate && (
                          <span className="text-xs text-gray-500 font-mono">
                            {driver.license_plate}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={VERIFICATION_STATUS_COLORS[driver.verification_status] || STATUS_COLORS.pending_review}>
                    {driver.verification_status.replace(/_/g, ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-gray-300 text-sm">
                    {driver.docs_uploaded_count} / 4
                  </span>
                  <span className="block text-[11px] text-gray-500">
                    + 1 optional
                  </span>
                </TableCell>
                <TableCell>
                  {driver.days_until_grace_ends > 0 ? (
                    <span className={`text-sm font-medium ${graceColor}`}>
                      {driver.days_until_grace_ends} day{driver.days_until_grace_ends !== 1 ? 's' : ''} left
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-red-400">
                      Expired
                    </span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Document Detail Drawer ──────────────────────────────────────────────────

function DocumentDetailDrawer({
  doc,
  onApprove,
  onReject,
  actionLoading,
}: {
  doc: DriverDocument;
  onApprove: (docId: string, overrides: { expiration?: string; docNumber?: string; fullName?: string }) => void;
  onReject: (docId: string) => void;
  actionLoading: boolean;
}) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [urlLoading, setUrlLoading] = useState(true);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [showRawJson, setShowRawJson] = useState(false);

  // Override fields
  const [overrideExpiration, setOverrideExpiration] = useState(doc.expiration_date || '');
  const [overrideDocNumber, setOverrideDocNumber] = useState(doc.document_number || '');
  const [overrideFullName, setOverrideFullName] = useState(doc.full_name_on_doc || '');

  // Generate signed URL — skipped for manual-entry rows since they
  // don't have a photo to view.
  useEffect(() => {
    let cancelled = false;
    const getUrl = async () => {
      if (!doc.storage_path) {
        if (!cancelled) {
          setSignedUrl(null);
          setUrlLoading(false);
          setUrlError(null);
        }
        return;
      }
      setUrlLoading(true);
      setUrlError(null);
      try {
        const { data, error } = await supabase
          .storage
          .from('driver-documents')
          .createSignedUrl(doc.storage_path, 60 * 5);
        if (error) throw error;
        if (!cancelled) setSignedUrl(data?.signedUrl || null);
      } catch (err: any) {
        if (!cancelled) setUrlError(err.message || 'Failed to load document');
      } finally {
        if (!cancelled) setUrlLoading(false);
      }
    };
    getUrl();
    return () => { cancelled = true; };
  }, [doc.storage_path]);

  const Icon = DOC_TYPE_ICONS[doc.doc_type] || FileText;
  const isPdf = doc.content_type === 'application/pdf';

  return (
    <div className="space-y-6">
      <SheetHeader>
        <SheetTitle className="text-white flex items-center gap-3">
          <UserAvatar size="md" name={doc.driver_name} src={doc.driver_picture_url} />
          <div>
            <p className="text-white font-semibold">{doc.driver_name || 'Unknown Driver'}</p>
            <div className="flex items-center gap-3 mt-0.5">
              {doc.driver_phone && (
                <span className="text-xs text-gray-400 font-mono">
                  {formatDominicaPhone(doc.driver_phone)}
                </span>
              )}
              {doc.license_plate && (
                <span className="text-xs text-gray-400 font-mono">{doc.license_plate}</span>
              )}
            </div>
          </div>
        </SheetTitle>
      </SheetHeader>

      {/* Document type header */}
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-emerald-400" />
        <span className="text-white font-medium">{DOC_TYPE_LABELS[doc.doc_type]}</span>
        <Badge className={STATUS_COLORS[doc.status] || STATUS_COLORS.pending_review}>
          {doc.status.replace(/_/g, ' ')}
        </Badge>
      </div>

      {/*
        Charter dispatch eligibility lives next to the regular
        verification controls because both are gated on documents the
        admin is reviewing right now (operator's license + contract
        carriage / charter permit).
      */}
      <CharterEligibilityToggle driverId={doc.driver_id} />

      {/* Attestation pledge — the legal record we collected when the
          driver submitted. Renders in every queue row regardless of
          submission method. */}
      {doc.attestation_signed_at && doc.attestation_typed_name ? (
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-emerald-500/15 p-2">
                <BadgeCheck className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">Driver attestation on file</p>
                <p className="mt-1 text-xs text-gray-400">
                  Driver typed their name and confirmed the pledge before
                  submission. This is the legal record.
                </p>
                <div className="mt-3 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Typed name</span>
                    <span className="text-white">{doc.attestation_typed_name}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Signed</span>
                    <span className="text-white">
                      {formatDistanceToNow(new Date(doc.attestation_signed_at), { addSuffix: true })}
                    </span>
                  </div>
                  {doc.attestation_device ? (
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Device</span>
                      <span className="text-white">{doc.attestation_device}</span>
                    </div>
                  ) : null}
                  {doc.submission_method ? (
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Method</span>
                      <span className="text-white">
                        {doc.submission_method === 'photo_and_manual'
                          ? 'Typed details + photo'
                          : doc.submission_method === 'manual_entry'
                            ? 'Typed only (legacy)'
                            : 'Photo only'}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Method banners — tell admin reviewers what they're looking at:
          • photo_and_manual: cross-check typed fields vs the photo. This
            is the default for every non-selfie doc on the current
            mobile build.
          • manual_entry (legacy): no photo, approve on attestation or
            reject asking for re-upload. Historical rows only.
       */}
      {doc.submission_method === 'photo_and_manual' ? (
        <Card className="bg-emerald-900/15 border-emerald-700/30">
          <CardContent className="py-3">
            <p className="text-sm text-emerald-200">
              <span className="font-semibold">Typed details + photo.</span>{' '}
              Cross-check the fields below against what's printed on the
              photo. Approve when they match; reject if the photo is
              unreadable or the fields don't match.
            </p>
          </CardContent>
        </Card>
      ) : doc.submission_method === 'manual_entry' ? (
        <Card className="bg-indigo-900/15 border-indigo-700/30">
          <CardContent className="py-3">
            <p className="text-sm text-indigo-200">
              <span className="font-semibold">Typed only (legacy).</span>{' '}
              This was submitted under the old typed-only model. No photo
              on file. Approve on the attestation pledge, or reject
              asking the driver to resubmit with a photo on the current
              build.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Two-column layout: Photo + AI Verdict */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* Photo (or "no photo" placeholder for manual-entry rows) */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-lg overflow-hidden">
          {!doc.storage_path ? (
            <div className="h-80 flex flex-col items-center justify-center gap-2 text-gray-500">
              <FileText className="w-10 h-10" />
              <p className="text-sm">No photo on file. The driver typed the data.</p>
              <p className="text-xs">Review the typed details on the right.</p>
            </div>
          ) : urlLoading ? (
            <div className="h-80 flex items-center justify-center">
              <Skeleton className="w-full h-full bg-gray-800" />
            </div>
          ) : urlError ? (
            <div className="h-80 flex items-center justify-center text-gray-500">
              <p>Preview unavailable: {urlError}</p>
            </div>
          ) : isPdf ? (
            <div className="h-80 flex flex-col items-center justify-center gap-3">
              <FileText className="w-12 h-12 text-gray-500" />
              <p className="text-gray-400 text-sm">PDF document</p>
              <a
                href={signedUrl!}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-emerald-400 text-sm hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
              </a>
            </div>
          ) : (
            <div className="relative">
              <img
                src={signedUrl!}
                alt={DOC_TYPE_LABELS[doc.doc_type]}
                className="w-full max-h-[500px] object-contain bg-black"
              />
              <a
                href={signedUrl!}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-md hover:bg-black/80 transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4 text-white" />
              </a>
            </div>
          )}
          <p className="text-[10px] text-gray-600 px-3 py-1.5">
            Downloading creates a copy outside the platform.
          </p>
        </div>

        {/* Verdict sidebar — AI panels only render when AI actually
            ran on this row (legacy data). For new uploads, AI is
            disabled and the admin enters the override fields below
            directly from the photo. */}
        <div className="space-y-4">
          {doc.ai_processed_at && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">AI Confidence (legacy)</p>
              <ConfidenceBar confidence={doc.ai_confidence} />
            </div>
          )}

          {doc.ai_warnings.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">AI Warnings (legacy)</p>
              <div className="flex flex-wrap gap-1.5">
                {doc.ai_warnings.map((w, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-md"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    {AI_WARNING_LABELS[w] || w}
                  </span>
                ))}
              </div>
            </div>
          )}

          {doc.ai_error && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">AI Error (legacy)</p>
              <p className="text-xs text-red-400">{doc.ai_error}</p>
            </div>
          )}

          {!doc.ai_processed_at && (
            <Card className="bg-blue-900/10 border-blue-700/30">
              <CardContent className="py-3">
                <p className="text-xs text-blue-300">
                  AI verification is disabled. Review the photo, fill
                  in the fields below, and approve or reject manually.
                </p>
              </CardContent>
            </Card>
          )}

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
              {doc.ai_processed_at ? 'Extracted Data' : 'Submitted Data'}
            </p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Name</span>
                <span className="text-white">{doc.full_name_on_doc || '–'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Number</span>
                <span className="text-white font-mono">{doc.document_number || '–'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Expires</span>
                <span className="text-white">{doc.expiration_date || '–'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Issued</span>
                <span className="text-white">{doc.issue_date || '–'}</span>
              </div>
            </div>
          </div>

          {doc.ai_extraction && (
            <div>
              <button
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                onClick={() => setShowRawJson(!showRawJson)}
              >
                {showRawJson ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Raw AI response (legacy)
              </button>
              {showRawJson && (
                <pre className="mt-2 p-2 bg-gray-900 border border-gray-800 rounded text-[10px] text-gray-400 overflow-auto max-h-48">
                  {JSON.stringify(doc.ai_extraction, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Override fields */}
      {(doc.status === 'manual_review' || doc.status === 'pending_review') && (
        <div className="space-y-4 border-t border-gray-800 pt-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">
            Override (admin can edit before approving)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-gray-400 text-xs">Expiration date</Label>
              <Input
                type="date"
                value={overrideExpiration}
                onChange={(e) => setOverrideExpiration(e.target.value)}
                className="bg-gray-900 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Document number</Label>
              <Input
                value={overrideDocNumber}
                onChange={(e) => setOverrideDocNumber(e.target.value)}
                className="bg-gray-900 border-gray-700 text-white mt-1"
                placeholder="e.g. PSV-2034"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Full name on doc</Label>
              <Input
                value={overrideFullName}
                onChange={(e) => setOverrideFullName(e.target.value)}
                className="bg-gray-900 border-gray-700 text-white mt-1"
                placeholder="e.g. Sarah Joseph"
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              onClick={() => handleApproveClick()}
              disabled={actionLoading}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {actionLoading ? 'Approving...' : 'Approve'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => onReject(doc.id)}
              disabled={actionLoading}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </div>
        </div>
      )}

      {/* Previous submissions — the audit trail.
          Lives at the bottom of the drawer so the reviewer sees the
          current submission first, then can drill into history if the
          context is unclear (e.g. driver re-submitting after a
          rejection, or third try at a license photo). Lazy-loads from
          the admin RPC the first time the section is expanded — most
          reviews don't need it so we don't pay the round-trip cost
          unless asked. */}
      <DriverDocumentHistorySection
        driverId={doc.driver_id}
        currentDocId={doc.id}
        currentDocType={doc.doc_type}
      />
    </div>
  );

  function handleApproveClick() {
    onApprove(doc.id, {
      expiration: overrideExpiration || undefined,
      docNumber: overrideDocNumber || undefined,
      fullName: overrideFullName || undefined,
    });
  }
}

// ─── Quick Start override (admin "I know this driver") ─────────────────────

/**
 * Compact card the admin can use to grant — or extend — a driver's
 * 90-day Quick Start grace window.
 *
 * The Quick Start floor (license + selfie) auto-starts a 90-day grace
 * window, but Dominica's small driver pool sometimes needs human
 * judgement: PSV renewal queues stalling, drivers the admin
 * personally knows, etc. This card is the manual lever for those
 * cases. All actions are audit-logged.
 */
function QuickStartOverrideCard({ driverId }: { driverId: string }) {
  const [days, setDays] = useState<number>(90);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [graceUntil, setGraceUntil] = useState<string | null>(null);

  // Pull the current grace_until so admin can see what they're about
  // to overwrite.
  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from('driver_details')
        .select('verification_grace_until')
        .eq('id', driverId)
        .maybeSingle();
      if (!error) setGraceUntil(data?.verification_grace_until ?? null);
    };
    void load();
  }, [driverId]);

  const apply = async () => {
    if (busy) return;
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.error('Enter a number of days between 1 and 365.');
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc('admin_grant_driver_quickstart', {
        p_driver_id: driverId,
        p_days: days,
        p_note: note.trim() || null,
      });
      if (error) throw error;
      const result = data as { ok: boolean; error?: string; grace_until?: string };
      if (!result?.ok) throw new Error(result?.error || 'Failed to apply override');
      toast.success(
        `Quick Start granted for ${days} days. Driver can run corridor work now.`,
      );
      setGraceUntil(result.grace_until ?? null);
      setNote('');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to apply override');
    } finally {
      setBusy(false);
    }
  };

  const graceLabel = (() => {
    if (!graceUntil) return 'No grace window set.';
    const end = new Date(graceUntil);
    const now = new Date();
    const days = Math.ceil((end.getTime() - now.getTime()) / 86_400_000);
    if (days <= 0) return `Grace ended ${end.toLocaleDateString()}`;
    return `Current grace ends ${end.toLocaleDateString()} (${days} day${days === 1 ? '' : 's'} left)`;
  })();

  return (
    <Card className="bg-emerald-900/10 border-emerald-700/30">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-emerald-500/15 p-2">
            <BadgeCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">
              I know this driver: grant Quick Start
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Lets them run regulated-fare corridor routes today, even if PSV
              and insurance aren't on file yet. Charter dispatch stays locked
              until full verification.
            </p>
            <p className="text-xs text-gray-500 mt-1.5">{graceLabel}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-gray-400">Days</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-gray-900 border-gray-700 text-white w-24 mt-1"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-gray-400">Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Driver from Roseau bus stand, vouched in person"
              className="bg-gray-900 border-gray-700 text-white mt-1"
            />
          </div>
          <Button
            onClick={apply}
            disabled={busy}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            {busy ? 'Applying…' : 'Apply'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Driver Docs Drawer (from Pending Drivers) ──────────────────────────────

function DriverDocsDrawer({
  driverId,
  onReviewDoc,
}: {
  driverId: string;
  onReviewDoc: (doc: DriverDocument) => void;
}) {
  const [docs, setDocs] = useState<DriverDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState<{ name: string | null; phone: string | null; plate: string | null; pic: string | null }>({
    name: null, phone: null, plate: null, pic: null,
  });

  useEffect(() => {
    const fetchDriverDocs = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('list_driver_documents_admin', {
          p_status: null,
          p_limit: 100,
          p_offset: 0,
        });
        if (error) throw error;
        const allDocs = data?.documents ?? (Array.isArray(data) ? data : []);
        const driverDocs = allDocs
          .filter((d: any) => d.driver_id === driverId)
          .map((d: any) => ({ ...d, ai_warnings: d.ai_warnings || [] }));
        setDocs(driverDocs);
        if (driverDocs.length > 0) {
          setDriver({
            name: driverDocs[0].driver_name,
            phone: driverDocs[0].driver_phone,
            plate: driverDocs[0].license_plate,
            pic: driverDocs[0].driver_picture_url,
          });
        }
      } catch (err: any) {
        toast.error(err.message || 'Failed to load driver documents');
      } finally {
        setLoading(false);
      }
    };
    fetchDriverDocs();
  }, [driverId]);

  const allDocTypes: DocType[] = [
    'drivers_license', 'psv_permit', 'vehicle_registration',
    'insurance_certificate', 'selfie_with_license',
  ];

  return (
    <div className="space-y-6">
      <SheetHeader>
        <SheetTitle className="text-white flex items-center gap-3">
          <UserAvatar size="md" name={driver.name} src={driver.pic} />
          <div>
            <p className="text-white font-semibold">{driver.name || 'Unknown Driver'}</p>
            <div className="flex items-center gap-3 mt-0.5">
              {driver.phone && (
                <span className="text-xs text-gray-400 font-mono">
                  {formatDominicaPhone(driver.phone)}
                </span>
              )}
              {driver.plate && (
                <span className="text-xs text-gray-400 font-mono">{driver.plate}</span>
              )}
            </div>
          </div>
        </SheetTitle>
      </SheetHeader>

      <QuickStartOverrideCard driverId={driverId} />

      <p className="text-xs text-gray-500 uppercase tracking-wider">
        Document Slots: 2 to start · 4 to fully verify · 1 optional
      </p>

      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 bg-gray-800 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {allDocTypes.map((docType) => {
            const doc = docs.find((d) => d.doc_type === docType);
            const Icon = DOC_TYPE_ICONS[docType];
            return (
              <div
                key={docType}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  doc
                    ? 'border-gray-700 bg-gray-900/30 cursor-pointer hover:bg-gray-800/50'
                    : 'border-gray-800 bg-gray-900/10'
                }`}
                onClick={() => doc && onReviewDoc(doc)}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${doc ? 'text-emerald-400' : 'text-gray-600'}`} />
                  <div>
                    <p className={`text-sm ${doc ? 'text-white' : 'text-gray-500'}`}>
                      {DOC_TYPE_LABELS[docType]}
                    </p>
                    {doc && (
                      <Badge className={`${STATUS_COLORS[doc.status]} text-[10px] mt-0.5`}>
                        {doc.status.replace(/_/g, ' ')}
                      </Badge>
                    )}
                  </div>
                </div>
                {doc ? (
                  <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white">
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                ) : (
                  <span className="text-xs text-gray-600">Not uploaded</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Confidence UI Components ────────────────────────────────────────────────

function ConfidencePill({ confidence }: { confidence: number | null }) {
  if (confidence === null) return <span className="text-xs text-gray-500">N/A</span>;
  const pct = Math.round(confidence * 100);
  const color = pct >= 85 ? 'text-emerald-400 bg-emerald-500/10' : pct >= 50 ? 'text-amber-400 bg-amber-500/10' : 'text-red-400 bg-red-500/10';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono ${color}`}>
      {pct}%
    </span>
  );
}

function ConfidenceBar({ confidence }: { confidence: number | null }) {
  if (confidence === null) {
    return <p className="text-xs text-gray-500">No AI analysis available</p>;
  }
  const pct = Math.round(confidence * 100);
  const barColor = pct >= 85 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-400">Confidence</span>
        <span className={pct >= 85 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}>
          {pct}%
        </span>
      </div>
      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Previous Submissions History ────────────────────────────────────────────

/**
 * Shape returned by the `admin_list_driver_document_history` RPC for
 * each historical row. The flags here let us render the right pill /
 * button without re-deriving state on the client.
 */
interface DriverDocumentHistoryRow {
  id: string;
  doc_type: DocType;
  status: DocStatus | 'expired';
  rejection_reason: string | null;
  storage_path: string | null;
  storage_purged_at: string | null;
  file_available: boolean;
  content_type: string | null;
  file_size_bytes: number | null;
  full_name_on_doc: string | null;
  document_number: string | null;
  issue_date: string | null;
  expiration_date: string | null;
  submission_method: 'photo' | 'manual_entry' | 'photo_and_manual' | null;
  attestation_typed_name: string | null;
  attestation_signed_at: string | null;
  reviewed_at: string | null;
  uploaded_at: string;
  is_active_row: boolean;
}

/**
 * "Previous submissions" timeline.
 *
 * Renders inside the per-document detail drawer. Lazy-loads the full
 * submission history for the driver via `admin_list_driver_document_history`
 * the first time it's expanded, then filters to the same doc_type as
 * the row the admin is currently reviewing — that's almost always the
 * comparison they actually want (e.g. "what name was on the previous
 * license they uploaded last month?"). The current row itself is
 * excluded so the timeline only shows what came BEFORE.
 *
 * Each historical entry renders:
 *   • Status pill (Replaced / Rejected / Expired / Approved)
 *   • Relative timestamp ("3 months ago")
 *   • Typed metadata (doc number, name, dates) for cross-comparison
 *   • Either a "View photo" link (signed URL, 5 min) if the file is
 *     still in Storage, OR a "[Photo purged on DATE]" badge if the
 *     90-day retention sweep has already deleted it.
 */
/**
 * Stringify whatever the Supabase client throws at us so the admin
 * sees a useful message instead of "[object Object]".
 *
 * Supabase's `PostgrestError` (returned in `{ error }` from rpc/select
 * calls) is a plain object with `{ message, details, hint, code }` —
 * NOT an Error instance — so `String(e)` and `e.message` on the bare
 * variable don't work. We try the standard surfaces in order:
 *   1. Error instance → use .message
 *   2. PostgrestError-ish → prefer .message, then .details, then .hint
 *   3. Plain object → JSON.stringify
 *   4. Last-ditch → String(e)
 */
function formatRpcError(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object') {
    const obj = e as Record<string, unknown>;
    const candidates = [obj.message, obj.error_description, obj.details, obj.hint];
    for (const v of candidates) {
      if (typeof v === 'string' && v.length > 0) return v;
    }
    try {
      const j = JSON.stringify(e);
      if (j && j !== '{}') return j;
    } catch {
      // fall through
    }
  }
  return String(e);
}

function DriverDocumentHistorySection({
  driverId,
  currentDocId,
  currentDocType,
}: {
  driverId: string;
  currentDocId: string;
  currentDocType: DocType;
}) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DriverDocumentHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Lazy fetch — only the first time the admin clicks to expand. We
  // intentionally do NOT refetch on every expand because the data is
  // stable for the lifetime of the drawer (the admin is reviewing a
  // single snapshot in time).
  useEffect(() => {
    if (!expanded || rows !== null) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: rpcErr } = await supabase.rpc(
          'admin_list_driver_document_history',
          { p_driver_id: driverId },
        );
        if (rpcErr) throw rpcErr;
        const payload = data as { ok?: boolean; error?: string; history?: DriverDocumentHistoryRow[] };
        if (!payload?.ok) throw new Error(payload?.error ?? 'failed to load history');
        if (!cancelled) setRows(payload.history ?? []);
      } catch (e) {
        // Supabase PostgrestError isn't an Error instance — it's a
        // plain object with { message, details, hint, code }. Plain
        // String(e) on that gives the useless "[object Object]" we
        // previously surfaced to the admin. Walk the common shapes
        // so the actual message lands in the UI.
        if (!cancelled) setError(formatRpcError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [expanded, rows, driverId]);

  // Filter to same doc_type AND drop the row currently displayed in
  // the drawer header. We surface the most-recent first to match
  // how admins naturally read a timeline.
  const priorRows = (rows ?? []).filter(
    (r) => r.doc_type === currentDocType && r.id !== currentDocId,
  );

  return (
    <Card className="bg-gray-900/40 border-gray-800 mt-6">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-900/60 transition-colors rounded-t-lg"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-white">
            Previous submissions
          </span>
          {rows !== null ? (
            <Badge className="bg-gray-800 text-gray-300 border-gray-700">
              {priorRows.length}
            </Badge>
          ) : null}
        </div>
        {expanded
          ? <ChevronDown className="w-4 h-4 text-gray-500" />
          : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>

      {expanded ? (
        <CardContent className="border-t border-gray-800 py-3 px-4 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Skeleton className="w-4 h-4 bg-gray-800 rounded-full" />
              Loading history…
            </div>
          ) : error ? (
            <p className="text-sm text-red-400">Couldn't load history: {error}</p>
          ) : priorRows.length === 0 ? (
            <p className="text-xs text-gray-500">
              No prior submissions on file for this document type. This is the driver's first attempt.
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500">
                Showing every {DOC_TYPE_LABELS[currentDocType]} this driver has previously submitted. Files older than 90 days are purged by the retention sweep, but the row stays for the audit trail.
              </p>
              <div className="space-y-2">
                {priorRows.map((row) => (
                  <HistoryRowItem key={row.id} row={row} />
                ))}
              </div>
            </>
          )}
        </CardContent>
      ) : null}
    </Card>
  );
}

function HistoryRowItem({ row }: { row: DriverDocumentHistoryRow }) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Generate the signed URL on-demand only — we don't want to fan out
  // 20 signed URL requests just because the admin expanded the panel.
  // 5-minute expiry is plenty for the admin to click through.
  //
  // The photo is rendered INLINE in this row (not opened in a new
  // tab) so popup blockers can't get in the way and the admin stays
  // in the verification context. We toggle `expanded` so the reviewer
  // can collapse it again to free vertical space when comparing many
  // historical rows.
  const togglePhoto = useCallback(async () => {
    if (!row.storage_path) return;
    if (expanded) {
      setExpanded(false);
      return;
    }
    if (signedUrl) {
      setExpanded(true);
      return;
    }
    setSigning(true);
    try {
      const { data, error } = await supabase
        .storage
        .from('driver-documents')
        .createSignedUrl(row.storage_path, 60 * 5);
      if (error) throw error;
      const url = data?.signedUrl ?? null;
      if (!url) throw new Error('No signed URL returned');
      setSignedUrl(url);
      setExpanded(true);
    } catch (e) {
      toast.error(
        e instanceof Error ? `Couldn't load photo: ${e.message}` : "Couldn't load photo",
      );
    } finally {
      setSigning(false);
    }
  }, [row.storage_path, expanded, signedUrl]);

  // Status pill copy — describes WHY this row is no longer the active
  // one so the admin can read the timeline at a glance.
  const statusBadge = (() => {
    if (row.rejection_reason === 'replaced_by_resubmission') {
      return {
        label: 'Replaced by re-submission',
        className: 'bg-gray-800 text-gray-300 border-gray-700',
      };
    }
    if (row.status === 'rejected') {
      return {
        label: 'Rejected by admin',
        className: 'bg-red-500/10 text-red-400 border-red-500/20',
      };
    }
    if (row.status === 'expired') {
      return {
        label: 'Expired',
        className: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      };
    }
    if (row.is_active_row) {
      return {
        label: 'Currently active',
        className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      };
    }
    return {
      label: row.status.replace(/_/g, ' '),
      className: STATUS_COLORS[row.status] ?? 'bg-gray-800 text-gray-300 border-gray-700',
    };
  })();

  return (
    <div className="rounded-md border border-gray-800 bg-gray-950/40 px-3 py-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
        <span className="text-xs text-gray-500">
          {formatDistanceToNow(new Date(row.uploaded_at), { addSuffix: true })}
        </span>
        {row.submission_method === 'photo_and_manual' ? (
          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
            Details + photo
          </Badge>
        ) : row.submission_method === 'manual_entry' ? (
          <Badge className="bg-indigo-500/10 text-indigo-300 border-indigo-500/20">
            Typed only (legacy)
          </Badge>
        ) : null}
      </div>

      {/* Metadata cross-comparison row. We deliberately include the
          typed values that admin uses to spot fraud — different name
          across submissions, conflicting doc numbers, etc. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
        {row.full_name_on_doc ? (
          <div className="flex justify-between gap-2">
            <span className="text-gray-500">Name on doc</span>
            <span className="text-gray-200">{row.full_name_on_doc}</span>
          </div>
        ) : null}
        {row.document_number ? (
          <div className="flex justify-between gap-2">
            <span className="text-gray-500">Doc number</span>
            <span className="text-gray-200 font-mono">{row.document_number}</span>
          </div>
        ) : null}
        {row.issue_date ? (
          <div className="flex justify-between gap-2">
            <span className="text-gray-500">Issued</span>
            <span className="text-gray-200">{row.issue_date}</span>
          </div>
        ) : null}
        {row.expiration_date ? (
          <div className="flex justify-between gap-2">
            <span className="text-gray-500">Expires</span>
            <span className="text-gray-200">{row.expiration_date}</span>
          </div>
        ) : null}
      </div>

      {/* Photo access — either an inline preview toggle, or a clear
          "purged" badge so the reviewer understands the photo is gone
          but the metadata is intact. */}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {row.file_available ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
              onClick={togglePhoto}
              disabled={signing}
            >
              <Eye className="w-3.5 h-3.5 mr-1.5" />
              {signing
                ? 'Loading…'
                : expanded
                  ? 'Hide photo'
                  : 'View photo'}
            </Button>
            {expanded && signedUrl ? (
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200"
              >
                <ExternalLink className="w-3 h-3" />
                Open full size
              </a>
            ) : null}
          </>
        ) : (
          <Badge className="bg-gray-800 text-gray-400 border-gray-700">
            <Trash2 className="w-3 h-3 mr-1" />
            {row.storage_purged_at
              ? `Photo purged ${formatDistanceToNow(new Date(row.storage_purged_at), { addSuffix: true })}`
              : 'No photo on file'}
          </Badge>
        )}
      </div>

      {expanded && signedUrl ? (
        <div className="mt-2 rounded-md border border-gray-800 bg-black/40 overflow-hidden">
          {/* max-h keeps the row from blowing out the drawer when the
              admin expands several historical photos at once. The
              image stays clickable via "Open full size" above for a
              true full-resolution view in a new tab. */}
          <img
            src={signedUrl}
            alt="Previous submission"
            className="block w-full max-h-[480px] object-contain bg-black"
            loading="lazy"
          />
        </div>
      ) : null}
    </div>
  );
}
